import { KycPreReviewTolerance } from '../../kyc-pre-review-modal/kyc-pre-review-modal.component';
import { SomosOcrScoreWeights } from './somos-precheck-score';

/**
 * Fallback "rough match" tolerance for the SomosKycV1 no-QR (Textract OCR) pre-check.
 *
 * The live values are read per KYC action from `cp_actions.config.somosOcrPreCheck` (see
 * SomosKycV1ProviderComponent.resolveOcrTolerance) so they can be tuned without a deploy. These
 * constants are only the fallback when the action config does not specify them. OCR is noisier than
 * a QR decode, so the comparison gets a slightly looser tolerance; each knob only LOOSENS the match.
 * The QR path passes no tolerance and keeps strict matching.
 */
export const SOMOS_OCR_PRE_REVIEW_TOLERANCE_DEFAULTS: KycPreReviewTolerance = {
  // Accept a name when at least this fraction of the shorter token set is found in the other
  // (covers an OCR-misread or dropped middle name).
  nameTokenOverlapThreshold: 0.6,
  // Allow up to this many misread CPF digits (same length) to still count as a match. 0 = exact.
  cpfMaxDigitDistance: 0,
};

/**
 * Fallback weighted-score weights/thresholds for the SomosKycV1 no-QR OCR pre-check decision
 * (skip the pre-review modal vs. show it). Like the tolerance above, the live values are read per
 * action from `cp_actions.config.somosOcrPreCheck` (see resolveOcrScoreWeights) so they can be
 * tuned without a deploy; each key falls back independently to the constant here.
 *
 * Threshold reasoning: with namePerTokenMatch = 5 and proceedThreshold = 9, a solid two-token name
 * match (first + last ≈ +10) auto-proceeds, and a name + CPF match (≈ +5 +5, plus the +2 joint
 * bonus) auto-proceeds. A lone CPF match (+5), a single matching name token (+5), or a matching CPF
 * sitting next to a different name (+5 − 1 = +4) all stay under 9 → 'review'. A clearly different
 * person (different valid CPF = −10, different name = −1) lands sharply negative.
 */
export const SOMOS_OCR_SCORE_WEIGHTS_DEFAULTS: SomosOcrScoreWeights = {
  // Name
  nameNotDetected: -1,
  namePerTokenMatch: 5,
  nameAllDifferent: -1,
  nameMinTokenLength: 2,
  nameTokenMinSimilarity: 0.6,
  // CPF
  cpfMatch: 5,
  cpfSimilarityMaxDistance: 2,
  cpfVeryDifferent: -10,
  cpfNotDetected: -1,
  // DOB
  dobMatch: 5,
  dobDayTolerance: 0,
  dobDifferent: -2,
  dobNotDetected: 0,
  // Combined
  nameAndCpfBothMatchBonus: 2,
  // Decision
  proceedThreshold: 9,
};
